// Admin: translated captions + location tags per gallery photo (image SEO).
import React, { useEffect, useState } from 'react';
import { Languages, Loader2, Sparkles, Save, Wand2, MapPin, Eye } from 'lucide-react';
import { apiCall } from './_shared';

const LANG_NAMES = { en: 'English', me: 'Crnogorski', sq: 'Shqip', de: 'Deutsch', ru: 'Русский', pl: 'Polski', hu: 'Magyar' };

const CaptionRow = ({ item, langs, onSaved }) => {
  const [caps, setCaps] = useState(item.caption_translations || {});
  const [loc, setLoc] = useState(item.location_tag || '');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const dirty = JSON.stringify(caps) !== JSON.stringify(item.caption_translations || {}) || loc !== (item.location_tag || '');

  const save = async () => {
    setBusy('save'); setMsg('');
    try { await apiCall(`/api/admin/gallery/captions/${item.id}`, { method: 'PUT', body: JSON.stringify({ caption_translations: caps, location_tag: loc }) }); setMsg('Saved'); onSaved(); }
    catch (e) { setMsg(e.message); } finally { setBusy(''); }
  };
  const translate = async () => {
    setBusy('ai'); setMsg('');
    try {
      if (dirty) await apiCall(`/api/admin/gallery/captions/${item.id}`, { method: 'PUT', body: JSON.stringify({ caption_translations: caps, location_tag: loc }) });
      const r = await apiCall(`/api/admin/gallery/captions/${item.id}/translate`, { method: 'POST' });
      setCaps(r.caption_translations); setMsg('Translated into 6 languages'); onSaved();
    } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  };

  const describe = async () => {
    setBusy('see'); setMsg('');
    try {
      const r = await apiCall(`/api/admin/gallery/captions/${item.id}/describe`, { method: 'POST' });
      setCaps(r.caption_translations); setMsg('AI described + translated'); onSaved();
    } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  };

  const done = langs.filter((l) => (caps[l] || '').trim()).length;
  const otherInputs = langs.filter((l) => l !== 'en').map((l) => (
    <label key={l} className="text-xs">
      <span className="text-slate-500 font-semibold">{LANG_NAMES[l]}</span>
      <input value={caps[l] || ''} onChange={(e) => setCaps((c) => ({ ...c, [l]: e.target.value }))} className="mt-0.5 w-full border rounded px-2 py-1" data-testid={`caption-input-${item.id}-${l}`} />
    </label>
  ));

  return (
    <li className="bg-white border rounded-xl p-3" data-testid={`caption-row-${item.id}`}>
      <div className="flex gap-3 items-start">
        <img src={item.url} alt={caps.en || item.alt} className="w-24 h-16 object-cover rounded-lg flex-shrink-0" loading="lazy" />
        <div className="flex-1 min-w-0 space-y-1.5">
          <p className="text-[11px] text-slate-500">#{item.id} · {item.category}{item.room_name ? ` · ${item.room_name}` : ''}</p>
          <input value={caps.en || ''} onChange={(e) => setCaps((c) => ({ ...c, en: e.target.value }))} placeholder="English caption (what is in the photo, where)"
            className="w-full border rounded-lg px-2.5 py-1.5 text-sm" data-testid={`caption-input-${item.id}-en`} />
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1 text-slate-500"><MapPin className="w-3 h-3" /></span>
            <input value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="Location tag, e.g. Velika Plaža, Ulcinj" className="border rounded px-2 py-1 w-64" data-testid={`caption-location-${item.id}`} />
            <span className={`px-1.5 py-0.5 rounded font-semibold ${done === langs.length ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-testid={`caption-langs-${item.id}`}>{done}/{langs.length} languages</span>
            <button type="button" onClick={() => setOpen((o) => !o)} className="text-slate-600 hover:underline" data-testid={`caption-toggle-${item.id}`}>{open ? 'Hide' : 'Edit'} translations</button>
            {item.caption_source && <span className={`px-1.5 py-0.5 rounded ${item.caption_source === 'ai' ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-600'}`} data-testid={`caption-source-${item.id}`}>{item.caption_source === 'ai' ? 'AI-described' : item.caption_source}</span>}
            <span className="flex-1" />
            <button type="button" onClick={describe} disabled={!!busy} className="inline-flex items-center gap-1 text-violet-700 font-semibold hover:underline disabled:opacity-50" title="GPT looks at the photo and writes the caption in all 7 languages" data-testid={`caption-describe-${item.id}`}>
              {busy === 'see' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />} AI describe photo
            </button>
            <button type="button" onClick={translate} disabled={!!busy || !(caps.en || '').trim()} className="inline-flex items-center gap-1 text-violet-700 font-semibold hover:underline disabled:opacity-50" data-testid={`caption-translate-${item.id}`}>
              {busy === 'ai' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} AI translate
            </button>
            {dirty && (
              <button type="button" onClick={save} disabled={!!busy} className="inline-flex items-center gap-1 bg-slate-900 text-white rounded px-2 py-1 font-semibold disabled:opacity-60" data-testid={`caption-save-${item.id}`}>
                {busy === 'save' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
              </button>
            )}
            {msg && <span className="text-emerald-700" data-testid={`caption-msg-${item.id}`}>{msg}</span>}
          </div>
          {open && <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 pt-1">{otherInputs}</div>}
        </div>
      </div>
    </li>
  );
};

const GalleryCaptions = ({ filter }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [job, setJob] = useState(null);
  const load = () => apiCall('/api/admin/gallery/captions').then(setData).catch((e) => setMsg(e.message));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    let timer;
    const poll = async () => {
      try {
        const j = await apiCall('/api/admin/gallery/captions/describe-all/status');
        setJob(j);
        if (j.status === 'running') timer = setTimeout(poll, 4000);
        else if (j.status === 'finished') load();
      } catch (e) { /* ignore */ }
    };
    poll();
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const describeAll = async (retryFailed = false) => {
    if (!retryFailed && !window.confirm('GPT will look at every photo that is not AI-described yet and write captions in 7 languages. This runs in the background (about 10–20 s per photo). Start?')) return;
    setMsg('');
    try {
      await apiCall(`/api/admin/gallery/captions/describe-all?only_templated=true${retryFailed ? '&retry_failed=true' : ''}`, { method: 'POST' });
      setJob({ status: 'running', done: 0, total: '…' });
      const poll = async () => { const j = await apiCall('/api/admin/gallery/captions/describe-all/status'); setJob(j); if (j.status === 'running') setTimeout(poll, 4000); else load(); };
      setTimeout(poll, 3000);
    } catch (e) { setMsg(e.message); }
  };
  const failReasons = Object.entries((job?.failed || []).reduce((m, f) => { const k = (f.error || 'unknown error').replace(/^skipped — /, ''); m[k] = (m[k] || 0) + 1; return m; }, {}))
    .sort((a, b) => b[1] - a[1]).slice(0, 4);

  const autoFill = async () => {
    setBusy(true); setMsg('');
    try { const r = await apiCall('/api/admin/gallery/captions/auto-fill', { method: 'POST' }); setMsg(`${r.filled} photos got captions in 7 languages`); load(); }
    catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  if (!data) return <p className="text-sm text-slate-500" data-testid="captions-loading">Loading captions…</p>;
  const items = data.gallery.filter((g) => filter === 'all' || g.category === filter);
  const rows = items.map((g) => <CaptionRow key={`${g.id}-${g.updated_at || ''}-${(g.caption_translations || {}).en || ''}`} item={g} langs={data.langs} onSaved={load} />);

  return (
    <div className="space-y-3" data-testid="gallery-captions">
      <div className="flex flex-wrap items-center gap-3 bg-slate-50 border rounded-xl px-4 py-3 text-sm">
        <Languages className="w-4 h-4 text-orange-500" />
        <span data-testid="captions-stats"><b>{data.with_caption}</b>/{data.total} captioned · <b>{data.complete}</b> complete in all {data.langs.length} languages · <b className="text-violet-700">{data.ai_described}</b> AI-described</span>
        <span className="flex-1" />
        {job?.status === 'running' ? (
          <span className="inline-flex items-center gap-2 text-violet-700 font-semibold" data-testid="describe-all-progress"><Loader2 className="w-4 h-4 animate-spin" /> Describing photos… {job.done}/{job.total}</span>
        ) : (
          <button type="button" onClick={() => describeAll(false)} className="inline-flex items-center gap-1 font-semibold text-white bg-violet-600 rounded-lg px-3 py-1.5 hover:bg-violet-700" title="GPT looks at each photo still using a templated caption and writes a real description in 7 languages" data-testid="captions-describe-all">
            <Eye className="w-4 h-4" /> AI describe all
          </button>
        )}
        {job?.status === 'finished' && job.failed?.length > 0 && (
          <span className="inline-flex items-center gap-2 text-amber-700 text-xs" data-testid="describe-all-failed">
            {job.failed.length} photo(s) failed
            <button type="button" onClick={() => describeAll(true)} className="font-semibold underline decoration-dotted hover:text-amber-900" data-testid="describe-all-retry-failed">Retry failed</button>
          </span>
        )}
        <button type="button" onClick={autoFill} disabled={busy} className="inline-flex items-center gap-1 font-semibold text-slate-800 border rounded-lg px-3 py-1.5 hover:bg-white disabled:opacity-60" title="Instant templated captions + location tag for every photo still missing one (no AI)" data-testid="captions-autofill">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Auto-fill missing
        </button>
        {msg && <span className="text-emerald-700" data-testid="captions-msg">{msg}</span>}
      </div>
      {failReasons.length > 0 && job?.status === 'finished' && (
        <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-900" data-testid="describe-all-fail-reasons">
          <b>Why the last run failed{job.stopped_early ? ' (stopped early after 8 failures in a row)' : ''}:</b>
          <ul className="list-disc ml-4 mt-1">{failReasons.map(([err, n]) => <li key={err}><span className="font-mono">{err}</span> — {n} photo(s)</li>)}</ul>
        </div>
      )}
      <p className="text-xs text-slate-500">Captions become the image alt text, the hover/lightbox caption and ImageObject structured data on /gallery — write what is in the photo and where it is (Google Images ranks descriptive, localised captions).</p>
      <ul className="space-y-2">{rows}</ul>
    </div>
  );
};

export default GalleryCaptions;
